import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';

import {
    Button,
    Flex,
    FormHelp,
    Label,
    Spacer,
    TextButton,
} from '@zillow/constellation';

import appLogger from 'app/shared/loggers/appLogger';
import NotificationConstants from 'app/shared/constants/NotificationConstants';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import PostingActions from 'app/shared/actions/PostingActions';
import {
    StyledAdornment,
    StyledAddAmenityInput,
    StyledAdornedInput,
    StyledIconCloseButton,
    StyledInput,
    StyledSpacer,
} from './StyledComponents';

const AdditionalAmenities = ({
    dispatch,
    label,
    previouslySavedAmenities = [],
    propertyId,
    updateFormData,
    updateStatusMessage,
}) => {
    const [tags, setTags] = useState([...previouslySavedAmenities]);
    const [tagToAdd, setTagToAdd] = useState('');
    const [editingIndex, setEditingIndex] = useState(null);
    const [duplicateTagError, setDuplicateTagError] = useState(false);

    useEffect(() => {
        updateFormData({ amenities: tags });
    }, [tags]);

    const addNewTagOnChangeHandler = (e) => {
        if (duplicateTagError) {
            setDuplicateTagError(false);
        }
        setTagToAdd(e.target.value);
    };

    const editingOnChangeHandler = (e, index) => {
        const tagsArray = [...tags];
        tagsArray[index].amenity = e.target.value;
        setTags(tagsArray);
    };

    const createAmenity = () => {
        const isDuplicateOfSavedTag = tags.some(tag => tag.amenity === tagToAdd);

        if (isDuplicateOfSavedTag) {
            setDuplicateTagError(true);
            return;
        }

        updateStatusMessage('Saving...');
        dispatch(PostingActions
            .property
            .amenity
            .create(
                {
                    propertyId,
                    amenityText: tagToAdd,
                },
                AnalyticsConstants.LISTING_COMPONENT_GROUP_NAME.AMENITIES
            ))
            .then((result) => {
                updateStatusMessage('Amenity created!', NotificationConstants.SUCCESS);
                setTags([...tags, { id: result.response.amenityId, amenity: tagToAdd }]);
            })
            .catch((error) => {
                const clientErrMsg = 'There was an error adding your amenity. Please try again.';
                updateStatusMessage(clientErrMsg, NotificationConstants.ERROR);
                appLogger.logError({
                    message: error.message,
                    context: 'PropertyAmenities.createAmenity',
                });
            })
            .finally(() => setTagToAdd(''));
    };

    const deleteAmenity = (index) => {
        const tagToDelete = tags[index];
        dispatch(PostingActions
            .property
            .amenity
            .delete(tagToDelete.id))
            .then((response) => {
                if (response.success) {
                    const tagsCopy = [...tags];
                    tagsCopy.splice(index, 1);
                    setTags(tagsCopy);
                } else {
                    const clientErrMsg = 'There was an error deleting your amenity. Please try again.';
                    updateStatusMessage(clientErrMsg, NotificationConstants.ERROR);
                    appLogger.logError({
                        message: response.error.message,
                        context: 'PropertyAmenities.deleteAmenity',
                    });
                }
            });
    };

    const updateAmenity = (index) => {
        updateStatusMessage('Saving...');
        const tagToUpdate = tags[index];
        dispatch(PostingActions
            .property
            .amenity
            .update(
                {
                    amenityId: tagToUpdate.id,
                    amenityText: tagToUpdate.amenity,
                }
            ))
            .then((result) => {
                updateStatusMessage('Amenity saved!', NotificationConstants.SUCCESS);
                const tagsArrayCopy = [...tags];
                // Update recently added amenity with id from API.
                tagsArrayCopy[index] = {
                    ...tagsArrayCopy[index],
                    id: result.response.amenityId,
                };
                setTags(tagsArrayCopy);
            })
            .catch((error) => {
                const clientErrMsg = 'There was an error updating your amenity. Please try again.';
                updateStatusMessage(clientErrMsg, NotificationConstants.ERROR);
                appLogger.logError({
                    message: error.message,
                    context: 'PropertyAmenities.updateAmenity',
                });
            })
            .finally(() => setEditingIndex(null));
    };

    const handleCreateAmenityKeyUp = (e) => {
        const keyCode = e.which || e.keyCode;
        if (keyCode === 13) {
            createAmenity();
        }
    };

    const handleSaveTagKeyUp = (e, index) => {
        const keyCode = e.which || e.keyCode;
        if (keyCode === 13) {
            updateAmenity(index);
        }
    };

    const handleDeleteAmenityKeyUp = (e, index) => {
        const keyCode = e.which || e.keyCode;
        if (keyCode === 13) {
            deleteAmenity(index);
        }
    };

    return (
        <div>
            <Spacer marginY="xs" />
            <Label>{label}</Label>
            <Spacer marginY="xs" />
            {tags.map((tag, i) => (
                <div key={`amenity-tag-${i}`}>
                    <Flex display="flex">
                        <StyledAdornedInput
                            size="sm"
                            inputId={`AdornedInput-AmenityTag-${i}`}
                            input={(
                                <StyledInput
                                    size="sm"
                                    onChange={e => editingOnChangeHandler(e, i)}
                                    onKeyUp={e => handleSaveTagKeyUp(e, i)}
                                    value={tag.amenity}
                                    disabled={editingIndex !== i}
                                />
                            )}
                            rightAdornment={
                                editingIndex !== i ? (
                                    <StyledAdornment
                                        className="adornmentWrapper"
                                        notEditing={editingIndex !== i}
                                        onClick={() => deleteAmenity(i)}
                                        onKeyUp={e => handleDeleteAmenityKeyUp(e, i)}
                                        tabIndex={editingIndex !== i ? 0 : ''}
                                    >
                                        <StyledIconCloseButton>
                                            <title>Delete</title>
                                        </StyledIconCloseButton>
                                    </StyledAdornment>
                                ) : <StyledAdornment className="adornmentWrapper"><StyledSpacer /></StyledAdornment>
                            }
                        />
                        <Spacer marginX="xs" />
                        {editingIndex === i && (
                            <React.Fragment>
                                <TextButton
                                    onClick={() => updateAmenity(i)}
                                    style={{ fontWeight: 400 }}
                                >
                                    Save
                                </TextButton>
                                <Spacer marginX="xs" />
                            </React.Fragment>
                        )}
                        {editingIndex !== i && (
                            <React.Fragment>
                                <TextButton
                                    onClick={() => setEditingIndex(i)}
                                    style={{ fontWeight: 400 }}
                                >
                                    Edit
                                </TextButton>
                                <Spacer marginX="xs" />
                            </React.Fragment>
                        )}
                    </Flex>
                    <Spacer marginY="sm" />
                </div>
            ))}
            <Flex display="flex">
                <StyledAddAmenityInput
                    aria-label="Describe amenity"
                    placeholder="Describe amenity"
                    size="sm"
                    onChange={addNewTagOnChangeHandler}
                    onKeyUp={handleCreateAmenityKeyUp}
                    value={tagToAdd}
                    error={duplicateTagError}
                />
                <Spacer marginX="xs" />
                <Button
                    onClick={createAmenity}
                    style={{ height: '34px', lineHeight: '0' }}
                    disabled={tagToAdd === ''}
                >
                    Add
                </Button>
            </Flex>
            {duplicateTagError && <FormHelp error={true}>Unable to save duplicate tags</FormHelp>}
            <Spacer marginY="xs" />
        </div>
    );
};

export default connect()(AdditionalAmenities);
